import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

// Configure axios interceptors for automatic auth
import "@/utils/axiosConfig";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import AuthCallback from "@/pages/AuthCallback";
import UserDashboard from "@/pages/UserDashboard";
import PricingPage from "@/pages/PricingPage";
import SuperAdminConfig from "@/pages/SuperAdminConfig";
import CompanyDetailsPage from "@/pages/CompanyDetailsPage";
import DemoPage from "@/pages/DemoPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import DeveloperDocsPage from "@/pages/DeveloperDocsPage";
import AdvancedLayoutBuilder from "@/pages/AdvancedLayoutBuilder";
import APIKeys from "@/pages/APIKeys";
import PublicWidget from "@/pages/PublicWidget";
import EmbedDesigner from "@/pages/EmbedDesigner";
import EmbedCheckout from "@/pages/EmbedCheckout";
import EmbeddableBuilder from "@/pages/EmbeddableBuilder";
import LayoutInfoPage from "@/pages/LayoutInfoPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import NotFoundPage from "@/pages/NotFoundPage";
import { Toaster } from "@/components/ui/sonner";

// Backoffice (Admin Panel) Pages
import BackofficeLogin from "@/pages/BackofficeLogin";
import BackofficeLayout from "@/pages/BackofficeLayout";
import BackofficeDashboard from "@/pages/BackofficeDashboard";
import BackofficeSupport from "@/pages/BackofficeSupport";
import BackofficeCustomers from "@/pages/BackofficeCustomers";
import BackofficeUsers from "@/pages/BackofficeUsers";
import BackofficeInvoices from "@/pages/BackofficeInvoices";
import BackofficeSettings from "@/pages/BackofficeSettings";
import BackofficeMarketing from "@/pages/BackofficeMarketing";
import BackofficeLeads from "@/pages/BackofficeLeads";
import BackofficeAudit from "@/pages/BackofficeAudit";
import BackofficeAvailability from "@/pages/BackofficeAvailability";
import BackofficeAccounts from "@/pages/BackofficeAccounts";
import BackofficeSubscriptions from "@/pages/BackofficeSubscriptions";
import BackofficeAnalytics from "@/pages/BackofficeAnalytics";
import BackofficeReferrals from "@/pages/BackofficeReferrals";
import InternalUserDetailsPage from "@/pages/InternalUserDetailsPage";
import BackofficeProtectedRoute from "@/components/BackofficeProtectedRoute";
import BackofficeProfile from "@/pages/BackofficeProfile";
import BackofficeApiDocs from "@/pages/BackofficeApiDocs";
import ReferralDashboard from "@/pages/ReferralDashboard";

// Component to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (from OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/widget/:layoutId" element={<PublicWidget />} />
      <Route path="/developer-docs" element={<DeveloperDocsPage />} />
      
      {/* Embeddable SDK routes (public - API key protected) */}
      <Route path="/embed/designer" element={<EmbedDesigner />} />
      <Route path="/embed/checkout/:layoutId" element={<EmbedCheckout />} />
      <Route path="/embed/builder" element={<EmbeddableBuilder />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/layouts" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/builder/:layoutId?" element={
        <ProtectedRoute>
          <AdvancedLayoutBuilder />
        </ProtectedRoute>
      } />
      
      <Route path="/layout-info/:layoutId" element={
        <ProtectedRoute>
          <LayoutInfoPage />
        </ProtectedRoute>
      } />
      
      <Route path="/api-keys" element={
        <ProtectedRoute>
          <APIKeys />
        </ProtectedRoute>
      } />
      
      <Route path="/referrals" element={
        <ProtectedRoute>
          <ReferralDashboard />
        </ProtectedRoute>
      } />
      
      {/* Legacy Super Admin routes - Redirect to new Backoffice */}
      <Route path="/super-admin" element={<Navigate to="/backoffice/dashboard" replace />} />
      <Route path="/super-admin/company/:companyId" element={<Navigate to="/backoffice/customers" replace />} />
      <Route path="/super-admin/config" element={<Navigate to="/backoffice/settings" replace />} />

      {/* Backoffice (Admin Panel) - Separate secure admin area */}
      {/* Obscure login path - not guessable */}
      <Route path="/secure-portal-x7k9m" element={<BackofficeLogin />} />
      
      {/* Block direct /backoffice/login access - show 404 */}
      <Route path="/backoffice/login" element={<BackofficeLogin />} />
      
      {/* Backoffice routes - PROTECTED: Only internal users allowed */}
      <Route path="/backoffice" element={
        <BackofficeProtectedRoute>
          <BackofficeLayout />
        </BackofficeProtectedRoute>
      }>
        <Route index element={<Navigate to="/backoffice/dashboard" replace />} />
        <Route path="dashboard" element={<BackofficeDashboard />} />
        <Route path="customers" element={<BackofficeCustomers />} />
        <Route path="customers/:companyId" element={<CompanyDetailsPage />} />
        <Route path="support" element={<BackofficeSupport />} />
        <Route path="users" element={<BackofficeUsers />} />
        <Route path="users/:userId" element={<InternalUserDetailsPage />} />
        <Route path="invoices" element={<BackofficeInvoices />} />
        <Route path="subscriptions" element={<BackofficeSubscriptions />} />
        <Route path="referrals" element={<BackofficeReferrals />} />
        <Route path="cms" element={<SuperAdminConfig />} />
        <Route path="marketing" element={<BackofficeMarketing />} />
        <Route path="leads" element={<BackofficeLeads />} />
        <Route path="analytics" element={<BackofficeAnalytics />} />
        <Route path="settings" element={<BackofficeSettings />} />
        <Route path="audit" element={<BackofficeAudit />} />
        <Route path="availability" element={<BackofficeAvailability />} />
        <Route path="accounts" element={<BackofficeAccounts />} />
        <Route path="profile" element={<BackofficeProfile />} />
        <Route path="api-docs" element={<BackofficeApiDocs />} />
      </Route>
      
      {/* 404 Catch-all route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
